import { useRef, useState, useEffect } from "react";

export default function CustomAudioPlayer({ audio, onTimeUpdate }) {
  const ref = useRef(null);
  const [isPlaying, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const togglePlay = () => {
    const el = ref.current;
    if (!el) return;
    isPlaying ? el.pause() : el.play();
    setPlaying(!isPlaying);
  };

  const setSpeed = (r) => {
    setRate(r);
    if (ref.current) {
      ref.current.playbackRate = r;
    }
  };

  return (
    <div className={`w-full ${isMobile ? 'max-w-full px-2' : 'max-w-md'} mx-auto mt-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm`}>
      {/* Hidden audio element */}
      <audio
        src={audio}
        ref={ref}
        onTimeUpdate={(e) => onTimeUpdate?.(e.target.currentTime)}
        onEnded={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      
      {/* Controls */}
      <div className={`flex items-center justify-between ${isMobile ? 'flex-col gap-4' : 'flex-row gap-6'}`}>
        {/* Play Button - Larger on mobile */}
        <button
          onClick={togglePlay}
          className={`bg-blue-100 hover:bg-blue-200 border border-blue-300 rounded-xl transition-all flex items-center justify-center ${
            isMobile 
              ? 'w-16 h-16 text-2xl' 
              : 'w-12 h-12 text-xl'
          }`}
        >
          {isPlaying ? "⏸" : "▶️"}
        </button>

        {/* Speed Controls */}
        <div className={`flex items-center gap-3 ${isMobile ? 'flex-col' : 'flex-row'}`}>
          {/* Speed Label */}
          <span className={`font-medium text-gray-700 whitespace-nowrap ${
            isMobile ? 'text-sm' : 'text-sm'
          }`}>
            Playback Speed
          </span>

          {/* Speed Buttons */}
          <div className="flex gap-2">
            {[1, 0.5, 0.25].map((r) => (
              <button
                key={r}
                onClick={() => setSpeed(r)}
                className={`font-medium rounded-lg border transition-all ${
                  isMobile 
                    ? 'px-3 py-2 text-sm' 
                    : 'px-3 py-1.5 text-sm'
                } ${
                  rate === r 
                    ? "bg-blue-500 text-white border-blue-500 shadow-sm" 
                    : "bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200"
                }`}
              >
                {r === 1 ? "Normal" : r === 0.5 ? "Slow" : "Slowest"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Current Speed Indicator - Only show on mobile for clarity */}
      {isMobile && (
        <div className="mt-3 text-center">
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
            Current: {rate === 1 ? "Normal" : rate === 0.5 ? "Slow" : "Slowest"} speed
          </span>
        </div>
      )}
    </div>
  );
}